"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import {
    Menu,
    X,
    Download,
    Vote,
    Gift,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Sparkles,
    Shield,
    Activity,
    LogOut,
    LayoutDashboard,
    LogIn,
    User
} from "lucide-react"
import { DragonIcon } from "@/components/ui/DragonIcon"
import { useSession, signOut } from "next-auth/react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import { NAVIGATION_PILLARS, QUICK_ACTION_CTA } from "@/lib/navigation"
import { NavDropdown } from "@/components/layout/NavDropdown"
import { UserNavDropdown } from "@/components/layout/UserNavDropdown"

const badgeStyles = {
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    purple: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    blue: "bg-[#C88246]/15 text-[#D8B478] border-[#C88246]/30",
}

export function Navbar() {
    const pathname = usePathname()
    const { data: session } = useSession()

    const [isMobileOpen, setIsMobileOpen] = React.useState(false)
    const [scrolled, setScrolled] = React.useState(false)
    const [activePillarHover, setActivePillarHover] = React.useState<string | null>(null)
    const [expandedMobilePillar, setExpandedMobilePillar] = React.useState<string | null>(null)

    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    React.useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20)
        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    // Prevent body scroll when mobile menu is open
    React.useEffect(() => {
        if (isMobileOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "unset"
        }
        return () => {
            document.body.style.overflow = "unset"
        }
    }, [isMobileOpen])

    const handlePillarEnter = (pillarId: string) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
        setActivePillarHover(pillarId)
    }

    const handlePillarLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setActivePillarHover(null)
        }, 180)
    }

    // Helper to determine if the current route belongs to a pillar
    const isPillarActive = (pillarId: string) => {
        const pillar = NAVIGATION_PILLARS.find(p => p.id === pillarId)
        if (!pillar) return false
        return pillar.items.some(item => {
            if (!item.href.startsWith("/")) return false
            if (item.href === "/" && pathname === "/") return true
            if (item.href !== "/" && pathname?.startsWith(item.href)) return true
            return false
        })
    }

    return (
        <nav
            className={cn(
                "fixed top-0 z-[100] w-full transition-all duration-300",
                scrolled
                    ? "bg-[#0B0908]/95 backdrop-blur-xl border-b border-[#3E3125] py-2.5 shadow-2xl shadow-black/80"
                    : "bg-[#0B0908]/60 backdrop-blur-md py-4 border-b border-[#3E3125]/30"
            )}
        >
            <div className="container mx-auto px-4">
                <div className="flex h-12 items-center justify-between gap-4">
                    {/* Brand Logo & Server Status Indicator */}
                    <div className="flex items-center gap-6 shrink-0">
                        <Link href="/" className="group flex items-center gap-3">
                            <DragonIcon
                                className="text-[#D8B478] h-10 w-10 drop-shadow-[0_0_15px_rgba(216,180,120,0.4)] group-hover:scale-110 transition-transform duration-300"
                                strokeWidth={2}
                            />
                            <span className="text-2xl lg:text-3xl font-black text-white tracking-tighter uppercase italic font-cinzel leading-none drop-shadow-md">
                                DRAGON<span className="text-[#C88246] text-glow-gold">L2</span>
                            </span>
                        </Link>
                    </div>

                    {/* Central 4 Thematic Pillars (Desktop) */}
                    <div className="hidden lg:flex items-center gap-1 xl:gap-2">
                        {NAVIGATION_PILLARS.map((pillar) => (
                            <NavDropdown
                                key={pillar.id}
                                pillar={pillar}
                                isActivePillar={isPillarActive(pillar.id)}
                                isOpen={activePillarHover === pillar.id}
                                onOpen={() => handlePillarEnter(pillar.id)}
                                onClose={handlePillarLeave}
                            />
                        ))}

                        {/* Fast 1-Click Vote Button (High Daily Value) */}
                        <Link
                            href="/vote"
                            className="hidden xl:flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-[11px] font-bold uppercase tracking-[0.15em] text-slate-300 hover:text-amber-300 hover:bg-slate-900 border border-transparent hover:border-amber-500/20 transition-all font-sans group"
                        >
                            <Vote className="w-3.5 h-3.5 text-emerald-400 group-hover:scale-110 transition-transform" />
                            <span>Vote</span>
                            <span className="text-[8px] font-extrabold uppercase px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                Free
                            </span>
                        </Link>
                    </div>

                    {/* Right Side High-Conversion CTAs & Player Hub (Desktop) */}
                    <div className="hidden lg:flex items-center gap-3 shrink-0">
                        {/* Download Client CTA */}
                        <Link href={QUICK_ACTION_CTA.href}>
                            <button
                                type="button"
                                className="relative group overflow-hidden rounded-xl p-px font-semibold text-xs transition-all active:scale-95 cursor-pointer"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-[#63503B] via-[#D8B478] to-[#8C6E4A] rounded-xl group-hover:opacity-100 transition-opacity opacity-80 animate-pulse-subtle" />
                                <span className="relative flex items-center gap-2 px-3.5 py-2 rounded-[11px] bg-[#14110F] group-hover:bg-[#1A1613] transition-colors">
                                    <Download className="w-4 h-4 text-[#D8B478] group-hover:-translate-y-0.5 transition-transform" />
                                    <div className="flex flex-col text-left">
                                        <span className="text-[11px] font-black uppercase tracking-wider text-[#EAD8B8] font-cinzel leading-tight">
                                            {QUICK_ACTION_CTA.label}
                                        </span>
                                        <span className="text-[8px] text-[#C88246] font-mono tracking-tighter">
                                            {QUICK_ACTION_CTA.sublabel}
                                        </span>
                                    </div>
                                </span>
                            </button>
                        </Link>

                        {/* Player Hub / Auth Widget */}
                        {session ? (
                            <UserNavDropdown user={session.user as any} />
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link
                                    href="/login"
                                    className="text-[11px] font-bold text-slate-300 hover:text-white uppercase tracking-[0.16em] py-2 px-3 hover:bg-slate-900/60 rounded-lg transition-colors font-sans"
                                >
                                    Log In
                                </Link>
                                <Link href="/register">
                                    <Button
                                        variant="sovereign"
                                        size="sm"
                                        className="h-9 px-4 font-bold text-[10px] uppercase tracking-widest font-sans"
                                    >
                                        Join Now
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile Top Bar Quick Actions: Auth + Menu Toggle */}
                    <div className="flex items-center gap-2 lg:hidden">
                        {session ? (
                            <div className="flex items-center gap-1.5">
                                <Link
                                    href="/dashboard"
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold font-sans"
                                    title="Go to Dashboard"
                                >
                                    <User className="w-3.5 h-3.5" />
                                    <span className="max-w-[70px] truncate">
                                        {session.user?.name || (session.user as any)?.login || "Hero"}
                                    </span>
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => signOut()}
                                    className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                                    title="Log Out"
                                    aria-label="Log Out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5">
                                <Link
                                    href="/login"
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-200 text-xs font-bold hover:text-white transition-colors"
                                >
                                    <LogIn className="w-3.5 h-3.5 text-amber-400" />
                                    <span>Log In</span>
                                </Link>
                            </div>
                        )}

                        {/* Mobile Menu Toggle Button */}
                        <button
                            type="button"
                            aria-label="Toggle navigation menu"
                            className="p-2 text-slate-400 hover:text-amber-400 transition-colors rounded-lg bg-slate-900/60 border border-slate-800"
                            onClick={() => setIsMobileOpen(!isMobileOpen)}
                        >
                            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Drawer Overlay */}
            <AnimatePresence>
                {isMobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "calc(100vh - 4rem)" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="lg:hidden fixed inset-x-0 top-16 bg-slate-950/98 backdrop-blur-2xl border-b border-amber-500/20 shadow-2xl overflow-y-auto z-50 flex flex-col justify-between"
                    >
                        <div className="container mx-auto px-4 py-6 space-y-6">
                            {/* Server Status Header Card */}
                            <div className="p-3.5 rounded-2xl bg-slate-900/80 border border-slate-800 flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                                        <DragonIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-white font-cinzel">
                                            DragonL2 Throne PvP 963x
                                        </div>
                                        <div className="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                            <span>All Systems Operational</span>
                                        </div>
                                    </div>
                                </div>
                                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                                    Online
                                </span>
                            </div>

                            {/* Mobile Quick Action Buttons */}
                            <div className="grid grid-cols-2 gap-3">
                                <Link
                                    href="/download"
                                    onClick={() => setIsMobileOpen(false)}
                                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-amber-600/20 font-sans"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>Download</span>
                                </Link>

                                <Link
                                    href="/#starter-pack"
                                    onClick={() => setIsMobileOpen(false)}
                                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-900 border border-amber-500/30 hover:border-amber-500 text-amber-300 font-bold text-xs uppercase tracking-wider font-sans"
                                >
                                    <Gift className="w-4 h-4 text-amber-400" />
                                    <span>Starter Pack</span>
                                </Link>
                            </div>

                            {/* Collapsible Thematic Accordions */}
                            <div className="space-y-2">
                                {NAVIGATION_PILLARS.map((pillar) => {
                                    const isExpanded = expandedMobilePillar === pillar.id
                                    const PillarIcon = pillar.icon

                                    return (
                                        <div
                                            key={pillar.id}
                                            className="rounded-2xl border border-slate-800/90 bg-slate-900/50 overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setExpandedMobilePillar(isExpanded ? null : pillar.id)}
                                                className="w-full flex items-center justify-between p-3.5 text-left text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-6 h-6 rounded-lg bg-slate-800 flex items-center justify-center text-amber-400">
                                                        <PillarIcon className="w-3.5 h-3.5" />
                                                    </div>
                                                    <span className="font-cinzel text-sm">{pillar.label}</span>
                                                </div>
                                                <ChevronDown className={cn(
                                                    "w-4 h-4 text-slate-500 transition-transform duration-200",
                                                    isExpanded && "rotate-180 text-amber-400"
                                                )} />
                                            </button>

                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="px-3 pb-3 space-y-1 border-t border-slate-800/60 pt-2"
                                                    >
                                                        {pillar.items.map((item) => {
                                                            const ItemIcon = item.icon
                                                            return (
                                                                <Link
                                                                    key={item.href}
                                                                    href={item.href}
                                                                    target={item.isExternal ? "_blank" : undefined}
                                                                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                                                                    onClick={() => setIsMobileOpen(false)}
                                                                    className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-800/60 transition-colors"
                                                                >
                                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                                        <ItemIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                                                        <div className="min-w-0">
                                                                            <div className="text-xs font-bold text-slate-200 truncate">
                                                                                {item.label}
                                                                            </div>
                                                                            <div className="text-[10px] text-slate-500 truncate">
                                                                                {item.description}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {item.badge && (
                                                                        <span className={cn(
                                                                            "text-[8px] font-extrabold uppercase px-1.5 py-0.2 rounded border shrink-0 ml-2",
                                                                            badgeStyles[item.badge.variant]
                                                                        )}>
                                                                            {item.badge.text}
                                                                        </span>
                                                                    )}
                                                                </Link>
                                                            )
                                                        })}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Direct Quick Link to Voting on Mobile */}
                            <Link
                                href="/vote"
                                onClick={() => setIsMobileOpen(false)}
                                className="flex items-center justify-between p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-bold text-xs uppercase tracking-wider"
                            >
                                <div className="flex items-center gap-2.5">
                                    <Vote className="w-4 h-4 text-emerald-400" />
                                    <span>Vote for Server Rewards</span>
                                </div>
                                <span className="text-[9px] font-extrabold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                    Free Daily
                                </span>
                            </Link>
                        </div>

                        {/* Mobile Auth Footer */}
                        <div className="p-4 border-t border-slate-900 bg-slate-950/90 mt-auto">
                            {session ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between px-2 text-xs text-slate-400">
                                        <span>Logged in as:</span>
                                        <span className="font-bold text-amber-400 font-cinzel">
                                            {session.user?.name || (session.user as any)?.login || "Hero"}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Link
                                            href="/dashboard"
                                            onClick={() => setIsMobileOpen(false)}
                                            className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-white uppercase tracking-wider"
                                        >
                                            <LayoutDashboard className="w-3.5 h-3.5" />
                                            <span>Dashboard</span>
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => signOut()}
                                            className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-400 uppercase tracking-wider"
                                        >
                                            <LogOut className="w-3.5 h-3.5" />
                                            <span>Log Out</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <Link
                                        href="/login"
                                        onClick={() => setIsMobileOpen(false)}
                                        className="flex items-center justify-center py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 uppercase tracking-wider"
                                    >
                                        Log In
                                    </Link>
                                    <Link
                                        href="/register"
                                        onClick={() => setIsMobileOpen(false)}
                                        className="flex items-center justify-center py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-md shadow-amber-600/20"
                                    >
                                        Register Free
                                    </Link>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    )
}
